import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { FiTarget } from 'react-icons/fi';
import WidgetHeader from './WidgetHeader';
import type { Goal, Task } from '../../types/work';

interface GoalProgressWidgetProps {
  goals: Goal[];
  tasks: Task[];
}

const GoalProgressWidget: React.FC<GoalProgressWidgetProps> = ({ goals, tasks }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  const activeGoals = goals.filter(g => g.status === 'active').slice(0, 5);

  // Compute progress per goal from tasks
  const goalProgress = (goalId: string) => {
    const goalTasks = tasks.filter(t => t.goal_id === goalId);
    if (goalTasks.length === 0) return { total: 0, completed: 0, overdue: 0, pct: 0 };
    const completed = goalTasks.filter(t => t.status === 'completed').length;
    const now = new Date().toISOString().slice(0, 10);
    const overdue = goalTasks.filter(t =>
      t.due_date && t.due_date.slice(0, 10) < now && t.status !== 'completed' && t.status !== 'cancelled'
    ).length;
    return { total: goalTasks.length, completed, overdue, pct: Math.round((completed / goalTasks.length) * 100) };
  };

  if (activeGoals.length === 0) {
    return (
      <div className={`rounded-lg border-l-4 border-emerald-500 p-4 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
        <WidgetHeader icon={<FiTarget className="w-4 h-4 text-emerald-400" />} title="Goals" accentColor="emerald" />
        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          No active goals. Create one to track strategic objectives.
        </p>
        <button
          onClick={() => navigate('/goals')}
          className="mt-2 text-xs text-emerald-400 hover:text-emerald-300 font-medium"
        >
          + New Goal
        </button>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border-l-4 border-emerald-500 p-4 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
      <div className="flex items-center gap-2 mb-3">
        <FiTarget className="w-4 h-4 text-emerald-400" />
        <span className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Goals</span>
      </div>

      <div className="space-y-3">
        {activeGoals.map(goal => {
          const prog = goalProgress(goal.id);
          return (
            <button
              key={goal.id}
              onClick={() => navigate('/goals')}
              className={`w-full text-left group ${darkMode ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'} rounded p-1.5 -m-1.5 transition-colors`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-medium truncate ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {goal.title}
                </span>
                <span className={`text-[10px] font-mono ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {prog.pct}%
                </span>
              </div>
              <div className={`h-1.5 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-500"
                  style={{ width: `${prog.pct}%` }}
                />
              </div>
              <div className="flex items-center gap-2 mt-1">
                {prog.overdue > 0 ? (
                  <span className="text-[10px] text-red-400 font-medium">{prog.overdue} overdue</span>
                ) : (
                  <span className="text-[10px] text-emerald-400">on track</span>
                )}
                {prog.total > 0 && (
                  <span className={`text-[10px] ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                    {prog.completed}/{prog.total} tasks
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {goals.filter(g => g.status === 'active').length > 5 && (
        <p className={`text-[10px] mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          +{goals.filter(g => g.status === 'active').length - 5} more goals
        </p>
      )}

      <button
        onClick={() => navigate('/goals')}
        className="mt-3 text-xs text-emerald-400 hover:text-emerald-300 font-medium"
      >
        View All Goals &rarr;
      </button>
    </div>
  );
};

export default GoalProgressWidget;
